import React from "react";
import { Link } from "react-router-dom";
import {
  rctiCalendarDate,
  rctiToday,
  validateRctiSupplyDate,
} from "utils/rctiDates";
import { isSignedAgreement } from "utils/rctiAgreementSigned";
import { formatAbn } from "utils/abn";
import { rctiTimestamp } from "utils/rctiTimestamp";
import { agreementReference } from "./RctiDesignSystem";
import "./rctiJourney.css";
export default function RCTIEligibilityReview({
  values,
  items,
  buyer,
  supplier,
  agreement,
  organisationName,
  currencyCode,
  onEdit,
  onNavigateTo,
  onContinue,
  onReadinessChange,
  adjustment = false,
}) {
  const money = (amount) =>
    `${currencyCode} ${Number(amount || 0).toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  let dateError = "";
  try {
    validateRctiSupplyDate(values.supplyDate, new Date(), adjustment);
  } catch (error) {
    dateError = error.message;
  }
  const day = rctiCalendarDate(values.supplyDate);
  const deadline = day
    ? new Date(new Date(`${day}T00:00:00Z`).getTime() + 28 * 86400000)
        .toISOString()
        .slice(0, 10)
    : "Not recorded";
  const dateLabel = (v) =>
    v && /^\d{4}-\d{2}-\d{2}$/.test(v)
      ? new Date(`${v}T00:00:00Z`).toLocaleDateString("en-AU", {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        })
      : v;
  const hasValidAbn = (value) =>
    /^\d{11}$/.test(String(value || "").replace(/\s/g, ""));
  const buyerPartyPass = Boolean(
    buyer &&
      hasValidAbn(buyer.abnNumber || values.buyerAbn) &&
      buyer.gstRegistered === true &&
      buyer.isLargeBusiness === false &&
      buyer.isGovernmentEntity === false
  );
  const supplierPartyPass = Boolean(
    supplier &&
      hasValidAbn(supplier.abnNumber || values.supplierAbn) &&
      supplier.gstRegistered === true &&
      supplier.isLargeBusiness === false &&
      supplier.isGovernmentEntity === false
  );
  const partyPass = buyerPartyPass && supplierPartyPass;
  const agreementPass = Boolean(
    agreement?.status === "active" && isSignedAgreement(agreement)
  );
  const valuesPass =
    items.length > 0 &&
    items.every(
      (item) =>
        Boolean(
          String(
            item.itemDescription || item.description || item.itemName || ""
          ).trim()
        ) &&
        Number(item.itemPrice) > 0 &&
        Number(item.itemQuantity) > 0
    );
  const taxPass =
    items.length > 0 &&
    values.gstType !== "No GST" &&
    items.every((item) => Number(item.gst) > 0);
  const buyerDeclarationPass = Boolean(
    buyer &&
      buyer.isLargeBusiness === false &&
      buyer.isGovernmentEntity === false
  );
  const checks = [
    [
      "ABNs, identities and GST registration",
      partyPass,
      `${organisationName} · ABN ${
        formatAbn(buyer?.abnNumber || values.buyerAbn) || "Not recorded"
      }. ${
        values.supplierCompanyName || values.supplierName || "Supplier"
      } · ABN ${
        formatAbn(supplier?.abnNumber || values.supplierAbn) || "Not recorded"
      }. Saved declarations are checked again on issue; this screen does not claim a new live registry result.`,
      partyPass ? "View party details" : "Edit party details",
    ],
    [
      "Current signed agreement covers this supply",
      agreementPass,
      agreement
        ? `${agreementReference(agreement._id)} · Wording v${
            agreement.version
          } · Accepted ${rctiTimestamp(
            agreement.supplierSignedAt
          )}. Covered supplies: ${agreement.scope}`
        : "A current signed agreement is required. The server checks agreement status, party eligibility and signed ABNs at issue.",
      agreementPass ? "View agreement" : "Edit agreement",
    ],
    [
      "Buyer determines the value",
      valuesPass,
      `Confirm the quantities, prices and discounts set by ${organisationName}. Subtotal ${money(
        values.totalAmount
      )}. Your authority confirmation on the next step covers these buyer-set values.`,
      valuesPass ? "View values" : "Edit values",
    ],
    [
      "Every line is taxable",
      taxPass,
      `${items.filter((item) => Number(item.gst) > 0).length} of ${
        items.length
      } lines have a positive GST rate. GST total ${money(
        values.totalGSTAmount
      )}.`,
      taxPass ? "View taxable items" : "Edit taxable items",
    ],
    [
      "Buyer declaration within supported pathway",
      buyerDeclarationPass,
      `${organisationName} must not be a large business or government entity. Saved GST and entity declarations are rechecked at issue; buyer-set values and taxable supplies require your final confirmation.`,
      buyerDeclarationPass
        ? "View buyer eligibility"
        : "Edit buyer eligibility",
    ],
    [
      "Supply date and issue window",
      !dateError,
      dateError ||
        (adjustment
          ? "The adjustment retains the original supply date. Issued history does not expire."
          : `Supply ${dateLabel(day)} · Today's issue ${dateLabel(
              rctiToday()
            )} is within the inclusive 28-day window.`),
      dateError ? "Edit dates" : "View dates",
    ],
  ];
  const sectionForAction = {
    "View values": "rcti-prepare-line-items",
    "Edit values": "rcti-prepare-line-items",
    "View taxable items": "rcti-prepare-line-items",
    "Edit taxable items": "rcti-prepare-line-items",
    "View dates": "rcti-prepare-dates",
    "Edit dates": "rcti-prepare-dates",
  };
  const partyActionHref = partyPass
    ? supplier?._id
      ? `/contacts/${supplier._id}`
      : "/contacts"
    : !buyerPartyPass
    ? "/editprofile"
    : supplier?._id
    ? `/contacts/editContact/${supplier._id}`
    : "/contacts";
  const agreementActionHref = agreement
    ? `/rcti-agreements/${agreement._id}`
    : supplier?._id
    ? `/rcti-agreements/new?supplier=${supplier._id}`
    : "/rcti-agreements";
  const blocked = checks.some(([, pass]) => pass === false);
  React.useEffect(() => {
    onReadinessChange?.(!blocked);
  }, [blocked, onReadinessChange]);
  return (
    <div className="rcti-review-eligibility">
      <div
        className={`rcti-design-notice rcti-eligibility-notice${
          blocked ? " is-blocked" : ""
        }`}
      >
        <strong>
          {blocked
            ? "Required checks need attention"
            : "Ready to review the required checks"}
        </strong>
        <p>
          Eligibility will be checked again when you issue. This review does not
          assign a legal number or create a payable.
        </p>
      </div>
      <div className="rcti-eligibility-stack">
        <section className="rcti-agreement-card">
          <h2>
            {blocked ? "Review required checks" : "Ready for final review"}
          </h2>
          <ul className="rcti-evidence-checks">
            {checks.map(([title, pass, description, action]) => (
              <li
                key={title}
                data-check={
                  pass === null ? "confirm" : pass ? "passed" : "blocked"
                }
              >
                <div>
                  <strong>{title}</strong>
                  <span
                    className={`rcti-evidence-status ${
                      pass === false ? "is-blocked" : ""
                    }`}
                  >
                    {pass === null
                      ? "Confirm at issue"
                      : pass
                      ? "Passed"
                      : "Action needed"}
                  </span>
                </div>
                <p>{description}</p>
                {action === "View party details" ||
                action === "Edit party details" ? (
                  <Link
                    to={partyActionHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {action}
                  </Link>
                ) : action === "View agreement" || action === "Edit agreement" ? (
                  <Link
                    to={agreementActionHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {action}
                  </Link>
                ) : action === "View buyer eligibility" ||
                  action === "Edit buyer eligibility" ? (
                  <Link
                    to={
                      buyerDeclarationPass
                        ? "/settings?tab=business"
                        : "/editprofile"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {action}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="rcti-evidence-edit"
                    onClick={() => {
                      const section = sectionForAction[action];
                      if (section && onNavigateTo) onNavigateTo(section);
                      else onEdit?.();
                    }}
                  >
                    {action}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
      <aside className="rcti-eligibility-stack">
        <section className="rcti-agreement-card">
          <h2>Draft summary</h2>
          <dl className="rcti-review-summary">
            <dt>Supplier</dt>
            <dd>
              {values.supplierCompanyName || values.supplierName || "Supplier"}
            </dd>
            <dt>Agreement</dt>
            <dd>
              {agreement
                ? `${agreementReference(agreement._id)} · v${agreement.version}`
                : "Required at issue"}
            </dd>
            <dt>Issue date</dt>
            <dd>{dateLabel(rctiToday())}</dd>
            <dt>Supply date</dt>
            <dd>{dateLabel(day) || "Not recorded"}</dd>
            <dt>Subtotal</dt>
            <dd>{money(values.totalAmount)}</dd>
            <dt>GST</dt>
            <dd>{money(values.totalGSTAmount)}</dd>
            <dt>Total</dt>
            <dd>
              <strong>{money(values.finalAmount)}</strong>
            </dd>
          </dl>
        </section>
        <section className="rcti-agreement-card">
          <h2>
            {adjustment
              ? "Correction of issued supply"
              : "Issue within 28 days"}
          </h2>
          <strong>
            {adjustment ? "Original supply date retained" : dateLabel(deadline)}
          </strong>
          <p>
            {adjustment
              ? "The prior version remains in history. The correction updates the existing payable."
              : "The issue window is measured from the supply date. This is not an expiry date for an issued RCTI."}
          </p>
        </section>
      </aside>
      <footer className="rcti-eligibility-footer">
        <button type="button" onClick={onEdit}>
          Back to draft
        </button>
        <button
          type="button"
          className="primary"
          onClick={onContinue}
          disabled={blocked}
        >
          Continue to preview &amp; issue
        </button>
      </footer>
    </div>
  );
}
